import { useState, useEffect, useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@altimateai/lego";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { createPlaygroundSchedule, updatePlaygroundSchedule } from "@/helpers/apis";
import { handleEmailDomainSaveError } from "@/helpers/handleSaveError";
import { DAYS_OF_WEEK } from "@/pages/subscriptions/components/wizard/steps/customizeSubscription/constants";
import {
  PlaygroundSchedule,
  PlaygroundContentScope,
  PlaygroundScheduleFrequency,
  playgroundContentScopeSchema,
  playgroundScheduleFrequencySchema,
} from "./types";
import { parseCronToFormValues } from "./parseCronToFormValues";

// Cron weekday values use the backend convention: Mon=1, Tue=2, ..., Sun=0.
export const DAY_OF_WEEK_OPTIONS = DAYS_OF_WEEK.map((label, index) => ({
  label,
  value: String(index === 6 ? 0 : index + 1),
}));

export interface ScheduleDialogProps {
  sessionId?: string;
  sessionName?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schedule?: PlaygroundSchedule;
  initialView?: "form" | "list";
}

export const FREQUENCY_OPTIONS: { label: string; value: PlaygroundScheduleFrequency }[] = [
  { label: "Daily", value: "daily" },
  { label: "Weekly", value: "weekly" },
  { label: "Monthly", value: "monthly" },
];

// 12-hour AM/PM labels mirror the subscription wizard's `TIME_OPTIONS`
// (`pages/subscriptions/.../constants.ts`) so the Studio dialog and the
// Subscriptions Schedule editor present hours identically. The stored value
// stays as the cron hour integer ("0"-"23") because the backend
// `PlaygroundScheduleCreateRequest.hour` is an int 0-23.
export const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => {
  const period = i < 12 ? "AM" : "PM";
  const display = i % 12 === 0 ? 12 : i % 12;
  return { label: `${display}:00 ${period}`, value: i.toString() };
});

export const CONTENT_SCOPE_OPTIONS: {
  label: string;
  value: PlaygroundContentScope;
  description: string;
  disabled?: boolean;
}[] = [
  {
    label: "Re-run Question",
    value: "first_query",
    description: "Re-run the original question and send fresh results",
  },
  {
    label: "Last Answer",
    value: "last_answer",
    description: "Coming soon — send only the most recent AI response",
    disabled: true,
  },
  {
    label: "Full Conversation",
    value: "full_chat",
    description: "Coming soon — send the entire conversation history",
    disabled: true,
  },
];

export const scheduleFormSchema = z
  .object({
    name: z
      .string()
      .min(1, "Please enter a schedule name")
      .max(255, "Schedule name must be 255 characters or less"),
    contentScope: playgroundContentScopeSchema,
    frequency: playgroundScheduleFrequencySchema.exclude(["custom"]),
    hour: z.string(),
    // Cron weekday string ("0"-"6"); Mon=1 default matches existing
    // backend behaviour. Stored as string for consistency with `hour`
    // and the shared DAY_OF_WEEK_OPTIONS values.
    dayOfWeek: z.string().default("1"),
    // Cron day-of-month string ("1"-"31"); 1 default matches existing
    // backend behaviour.
    dayOfMonth: z.string().default("1"),
    slackWebhookId: z.string().optional().default(""),
    emails: z.string().default(""),
  })
  .refine(
    data => {
      const hasEmails = data.emails.trim().length > 0;
      const hasWebhook = data.slackWebhookId.length > 0;
      return hasEmails || hasWebhook;
    },
    { message: "Please enter at least one email or select a Slack channel", path: ["emails"] }
  )
  .refine(
    data => {
      if (data.emails.trim().length === 0) return true;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return data.emails
        .split(",")
        .map(e => e.trim())
        .filter(Boolean)
        .every(e => emailRegex.test(e));
    },
    { message: "One or more email addresses are invalid", path: ["emails"] }
  );

export type ScheduleFormValues = z.infer<typeof scheduleFormSchema>;

const parseEmails = (emails: string) =>
  emails
    .split(",")
    .map(e => e.trim())
    .filter(Boolean);

// Build the schedule payload for create / update requests. Monthly days are
// plain `day_of_month` integers (1-31).
const buildSchedulePayload = (
  values: ScheduleFormValues
): {
  frequency: PlaygroundScheduleFrequency;
  hour: number;
  day_of_week?: number;
  day_of_month?: number;
} => {
  const hour = parseInt(values.hour);
  if (values.frequency === "weekly") {
    return { frequency: values.frequency, hour, day_of_week: Number(values.dayOfWeek) };
  }
  if (values.frequency === "monthly") {
    return { frequency: values.frequency, hour, day_of_month: Number(values.dayOfMonth) };
  }
  return { frequency: values.frequency, hour };
};

type DialogView = "form" | "success" | "list";

export const useScheduleDialog = (
  sessionId: string | undefined,
  sessionName: string | undefined,
  open: boolean,
  onOpenChange: (open: boolean) => void,
  schedule?: PlaygroundSchedule,
  initialView: DialogView = "form"
) => {
  const queryClient = useQueryClient();
  const [editingSchedule, setEditingSchedule] = useState<PlaygroundSchedule | undefined>(schedule);
  const isEditing = !!editingSchedule;
  const [view, setView] = useState<DialogView>(initialView);

  const form = useForm<ScheduleFormValues>({
    resolver: zodResolver(scheduleFormSchema),
    defaultValues: parseCronToFormValues(schedule, sessionName ?? ""),
  });

  useEffect(() => {
    setEditingSchedule(schedule);
  }, [schedule]);

  useEffect(() => {
    if (open) {
      setView(initialView);
      form.reset(parseCronToFormValues(editingSchedule, sessionName ?? ""));
    }
  }, [open, editingSchedule, sessionName, form, initialView]);

  const resetToNewForm = useCallback(() => {
    setEditingSchedule(undefined);
    form.reset(parseCronToFormValues(undefined, sessionName ?? ""));
    setView("form");
  }, [form, sessionName]);

  const { mutate: createSchedule, isPending: isCreating } = useMutation({
    mutationFn: (values: ScheduleFormValues) => {
      // Only include optional fields when they carry a value — the backend
      // schema treats `emails` and `slack_webhook_id` as omissible, and
      // sending an empty array for emails in Slack-only schedules risks
      // the backend rejecting it if validation ever tightens.
      const parsedEmails = parseEmails(values.emails);
      return createPlaygroundSchedule({
        name: values.name,
        session_id: sessionId!,
        content_scope: values.contentScope,
        ...buildSchedulePayload(values),
        ...(parsedEmails.length > 0 ? { emails: parsedEmails } : {}),
        ...(values.slackWebhookId ? { slack_webhook_id: Number(values.slackWebhookId) } : {}),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["playgroundSchedules"] });
      toast({ title: "Schedule created successfully" });
      setView("success");
    },
    onError: (error: unknown) =>
      handleEmailDomainSaveError(form.setError, "emails", error, "Failed to create schedule"),
  });

  const { mutate: updateSchedule, isPending: isUpdating } = useMutation({
    mutationFn: (values: ScheduleFormValues) =>
      updatePlaygroundSchedule(editingSchedule!.id, {
        name: values.name,
        content_scope: values.contentScope,
        ...buildSchedulePayload(values),
        emails: parseEmails(values.emails),
        // Backend PATCH semantics: 0 clears the webhook, omitted/null leaves
        // it unchanged. We always send 0 when the user has no webhook
        // selected so clearing an existing webhook actually clears it.
        slack_webhook_id: values.slackWebhookId ? Number(values.slackWebhookId) : 0,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["playgroundSchedules"] });
      toast({ title: "Schedule updated successfully" });
      onOpenChange(false);
    },
    onError: (error: unknown) =>
      handleEmailDomainSaveError(form.setError, "emails", error, "Failed to update schedule"),
  });

  const onSubmit = (values: ScheduleFormValues) => {
    if (isEditing) {
      updateSchedule(values);
    } else {
      if (!sessionId) return;
      createSchedule(values);
    }
  };

  return {
    form,
    view,
    setView,
    isEditing,
    isPending: isCreating || isUpdating,
    formValues: form.getValues(),
    onSubmit,
    resetToNewForm,
    setEditingSchedule,
  };
};
